import AuthedImage from '../../components/AuthedImage';
import { openWithAuth } from '../../utils/authedResource';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Form, Input, Select, DatePicker, Upload, Button, Card, Row, Col, message, Space, Modal, Popconfirm, List, Tag, Descriptions, theme, Tabs, Table, Statistic, Divider, Grid } from 'antd';
import { UploadOutlined, SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ExportOutlined, PrinterOutlined, ClockCircleOutlined, QrcodeOutlined } from '@ant-design/icons';
import { Pie, Column } from '@ant-design/plots';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/http';
import dayjs from 'dayjs';
import { BrowserQRCodeReader } from '@zxing/browser';

const { Option } = Select;
const { TextArea } = Input;

const getBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);
});

const isProbablyImageUrl = (url, name) => {
  const u = String(url || '').toLowerCase();
  const n = String(name || '').toLowerCase();
  const s = u || n;
  return /(\.png|\.jpg|\.jpeg|\.gif|\.webp|\.bmp|\.svg)(\?|#|$)/i.test(s);
};

const parseJsonIfPossible = (value) => {
  if (!value) return null;
  if (typeof value !== 'string') return null;
  const s = value.trim();
  if (!s) return null;
  if (!(s.startsWith('{') || s.startsWith('['))) return null;
  try { return JSON.parse(s); } catch (e) { return null; }
};

const parseControlMeasuresToList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(v => String(v || '').trim()).filter(Boolean);
  const s = String(value);
  // Common separators: newline / Chinese semicolon / semicolon / comma / enumeration.
  const parts = s.split(/\r?\n|；|;|，|,|、/).map(v => String(v || '').trim()).filter(Boolean);
  // Dedup while keeping order
  const seen = new Set();
  const out = [];
  for (const p of parts) {
    if (!seen.has(p)) { seen.add(p); out.push(p); }
  }
  return out;
};

const ABNORMAL_RESULT_SET = new Set(['abnormal', 'noncompliant', 'invalid']);

const buildAbnormalItemKey = (item) => {
  const placePart = String(item?.placePart || '').trim();
  const measure = String(item?.measure || '').trim();
  return `${placePart}||${measure}`;
};

const parseAbnormalItemKey = (key) => {
  const s = String(key || '');
  const idx = s.indexOf('||');
  if (idx < 0) return { placePart: '', measure: s };
  return { placePart: s.slice(0, idx), measure: s.slice(idx + 2) };
};

const getMeasureResultLabel = (value) => {
  const v = value == null ? '' : String(value);
  const hit = MEASURE_RESULT_OPTIONS.find(o => String(o.value) === v);
  return hit?.label || (v ? v : '—');
};

const deriveOverallStatusFromChecks = (checks) => {
  const arr = Array.isArray(checks) ? checks : [];
  if (arr.length === 0) return 'PENDING';

  // 1) 优先使用每条的显式 status（如果业务上有填）
  const normalized = arr
    .map(it => String(it?.status || '').toUpperCase())
    .filter(Boolean);
  if (normalized.length > 0) {
    if (normalized.every(s => s === 'COMPLETED')) return 'COMPLETED';
    if (normalized.some(s => s === 'IN_PROGRESS')) return 'IN_PROGRESS';
    return 'PENDING';
  }

  // 2) 若未维护 status，则根据检查结果推导：有异常 => IN_PROGRESS；全部有结果且无异常 => COMPLETED；否则 PENDING
  const hasAnyAbnormal = arr.some(it => {
    const r = String(it?.result || '').trim();
    const isAbnormalByResult = ABNORMAL_RESULT_SET.has(r);
    const hasAbDesc = !!String(it?.abnormal_description || '').trim();
    return isAbnormalByResult || hasAbDesc;
  });
  if (hasAnyAbnormal) return 'IN_PROGRESS';

  const hasAnyMissingResult = arr.some(it => !String(it?.result || '').trim());
  if (hasAnyMissingResult) return 'PENDING';
  return 'COMPLETED';
};

const deriveOverallRectificationResponsibleFromChecks = (checks) => {
  const arr = Array.isArray(checks) ? checks : [];
  // 优先取“异常项”的责任人，否则取第一个非空
  const abnormal = arr.find(it => ABNORMAL_RESULT_SET.has(String(it?.result || '').trim()) || String(it?.abnormal_description || '').trim());
  const first = abnormal || arr.find(it => String(it?.rectification_responsible || '').trim());
  return first?.rectification_responsible || null;
};

const deriveOverallRectificationDeadlineFromChecks = (checks) => {
  const arr = Array.isArray(checks) ? checks : [];
  const dates = arr
    .map(it => it?.rectification_deadline)
    .filter(Boolean)
    .map(d => (dayjs.isDayjs(d) ? d : dayjs(d)))
    .filter(d => d && d.isValid());
  if (dates.length === 0) return null;
  return dates.reduce((min, cur) => (cur.isBefore(min) ? cur : min), dates[0]);
};

const abnormalCheckItems = (record) => {
  if (!record) return [];

  const measuresObj = parseJsonIfPossible(record?.inspection_measures);
  const resultObj = parseJsonIfPossible(record?.inspection_result);

  const methodByMeasure = new Map();
  const resultByMeasure = new Map();
  const abnormalDescByMeasure = new Map();

  // methods / abnormal_description may exist in measures
  if (Array.isArray(measuresObj?.items)) {
    for (const it of measuresObj.items) {
      const m = String(it?.measure || '').trim();
      if (!m) continue;
      const placePart = String(it?.place_part || it?.placePart || '').trim();
      const method = String(it?.method || '').trim();
      if (method) methodByMeasure.set(m, method);

      const r = it?.result;
      if (r != null && String(r).trim()) resultByMeasure.set(m, r);

      const ab = String(it?.abnormal_description || '').trim();
      if (ab) abnormalDescByMeasure.set(m, ab);

      // place/part (used for filtering)
      if (placePart) {
        // store as a synthetic method key suffix via a dedicated map
        // (kept simple: attach to output later by reading from measures items)
      }
    }
  }

  // results / abnormal_description may exist in result structure
  if (Array.isArray(resultObj?.results)) {
    for (const r of resultObj.results) {
      const m = String(r?.measure || '').trim();
      if (!m) continue;
      const v = r?.result;
      if (v != null && String(v).trim()) resultByMeasure.set(m, v);
      const ab = String(r?.abnormal_description || '').trim();
      if (ab) abnormalDescByMeasure.set(m, ab);
    }
  }

  // collect abnormal measures
  const allMeasures = new Set([
    ...Array.from(methodByMeasure.keys()),
    ...Array.from(resultByMeasure.keys()),
    ...Array.from(abnormalDescByMeasure.keys())
  ]);

  const out = [];
  for (const measure of allMeasures) {
    const result = resultByMeasure.get(measure);
    const abnormalDescription = abnormalDescByMeasure.get(measure);

    const isAbnormalByResult = ABNORMAL_RESULT_SET.has(String(result || '').trim());
    const isAbnormal = isAbnormalByResult || !!String(abnormalDescription || '').trim();
    if (!isAbnormal) continue;

    // 取该措施在 measures.items 中的场所/部位（如果有）
    let placePart = '';
    if (Array.isArray(measuresObj?.items)) {
      const hit = measuresObj.items.find(it => String(it?.measure || '').trim() === measure);
      placePart = String(hit?.place_part || hit?.placePart || '').trim();
    }

    out.push({
      measure,
      placePart,
      method: methodByMeasure.get(measure) || '',
      result,
      resultLabel: getMeasureResultLabel(result),
      abnormalDescription: abnormalDescription || ''
    });
  }

  return out;
};

const buildGovernAbnormalMeasureText = (record) => {
  const list = abnormalCheckItems(record);
  if (!list.length) return '';
  return list
    .map(i => {
      const m = String(i?.measure || '').trim();
      if (!m) return '';
      const method = String(i?.method || '').trim();
      const r = String(i?.resultLabel || '').trim();
      // 把“检查方法/结果”也带出来，减少手填
      return method || r ? `${m}（方法：${method || '—'}；结果：${r || '—'}）` : m;
    })
    .filter(Boolean)
    .join('\n');
};

const buildGovernAbnormalDescriptionText = (record) => {
  const list = abnormalCheckItems(record);
  if (!list.length) return '';
  return list
    .map(i => {
      const m = String(i?.measure || '').trim();
      const ab = String(i?.abnormalDescription || '').trim();
      if (!m && !ab) return '';
      if (!m) return ab;
      return `${m}：${ab || '—'}`;
    })
    .filter(Boolean)
    .join('\n');
};

const MEASURE_RESULT_OPTIONS = [
  { label: '正常', value: 'normal' },
  { label: '异常', value: 'abnormal' },
  { label: '合规', value: 'compliant' },
  { label: '不合规', value: 'noncompliant' },
  { label: '失效', value: 'invalid' }
];

// 风险等级选项
const RISK_LEVELS = [
  { label: '低风险', value: 'LOW' },
  { label: '中风险', value: 'MEDIUM' },
  { label: '高风险', value: 'HIGH' }
];

// 状态选项
const STATUS_OPTIONS = [
  { label: '待整改', value: 'PENDING' },
  { label: '整改中', value: 'IN_PROGRESS' },
  { label: '已完成', value: 'COMPLETED' }
];

const DormHazardPage = () => {
  const { user, hasPermission } = useAuth();
  const [form] = Form.useForm();
  const [governForm] = Form.useForm();
  const [approveForm] = Form.useForm();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hazardList, setHazardList] = useState([]);
  const [responsibleOptions, setResponsibleOptions] = useState([]);
  const [responsibleLoading, setResponsibleLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentHazard, setCurrentHazard] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedRiskLevel, setSelectedRiskLevel] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [riskPoints, setRiskPoints] = useState([]);
  const [governModalVisible, setGovernModalVisible] = useState(false);
  const [governSubmitting, setGovernSubmitting] = useState(false);
  const [governPhotoList, setGovernPhotoList] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailHazard, setDetailHazard] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('inspection');

  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [approveRecord, setApproveRecord] = useState(null);
  const [approveCandidates, setApproveCandidates] = useState([]);

  const [hazardDataLoading, setHazardDataLoading] = useState(false);
  const [hazardData, setHazardData] = useState([]);
  const [hazardDataSummary, setHazardDataSummary] = useState(null);

  const [governLoading, setGovernLoading] = useState(false);
  const [governList, setGovernList] = useState([]);

  const [closeLoading, setCloseLoading] = useState(false);
  const [closeList, setCloseList] = useState([]);

  const [scanModalVisible, setScanModalVisible] = useState(false);
  const [scanError, setScanError] = useState('');
  const [scanDevices, setScanDevices] = useState([]);
  const [scanDevicesLoading, setScanDevicesLoading] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [scanOnlyMode, setScanOnlyMode] = useState(false);
  const [scanSessionId, setScanSessionId] = useState(0);
  const scanVideoRef = useRef(null);
  const scanReaderRef = useRef(null);
  const scanControlsRef = useRef(null);
  const handleScanTextRef = useRef(null);

  const preferBackCameraDeviceId = useCallback((devices) => {
    const list = Array.isArray(devices) ? devices : [];
    if (!list.length) return '';
    const byLabel = (d) => String(d?.label || '').toLowerCase();
    const back = list.find(d => /back|rear|environment|后置|后摄|主摄/.test(byLabel(d)));
    return (back?.deviceId || list[0]?.deviceId || '');
  }, []);

  const sleep = useCallback((ms) => new Promise(resolve => setTimeout(resolve, ms)), []);

  const waitForVideoEl = useCallback(async (timeoutMs = 800) => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (scanVideoRef.current) return scanVideoRef.current;
      // allow DOM to paint
      // eslint-disable-next-line no-await-in-loop
      await sleep(16);
    }
    return scanVideoRef.current;
  }, [sleep]);

  const stopScan = useCallback(() => {
    try {
      scanControlsRef.current?.stop?.();
    } catch (e) {
      // ignore
    }
    scanControlsRef.current = null;

    try {
      scanReaderRef.current?.reset?.();
    } catch (e) {
      // ignore
    }
    scanReaderRef.current = null;

    const video = scanVideoRef.current;
    if (video) {
      try { video.pause?.(); } catch (e) { /* ignore */ }
      if (video.srcObject) {
        try {
          const tracks = video.srcObject.getTracks?.() || [];
          tracks.forEach(track => {
            try { track.stop?.(); } catch (e) { /* ignore */ }
          });
        } catch (e) {
          // ignore
        }
      }
      try { video.srcObject = null; } catch (e) { /* ignore */ }
      try { video.src = ''; } catch (e) { /* ignore */ }
    }
  }, []);

  const governAbnormalItems = abnormalCheckItems(currentHazard);

  const watchedGovernPlacePart = Form.useWatch('rectification_place_part', governForm);

  const governFilteredAbnormalItems = useMemo(() => {
    const placePart = String(watchedGovernPlacePart || '').trim();
    if (!placePart) return governAbnormalItems;
    const hit = governAbnormalItems.filter(it => String(it?.placePart || '').trim() === placePart);
    return hit.length ? hit : governAbnormalItems;
  }, [governAbnormalItems, watchedGovernPlacePart]);

  const governPlacePartOptions = useMemo(() => {
    const set = new Set();
    for (const it of governAbnormalItems) {
      const p = String(it?.placePart || '').trim();
      if (p) set.add(p);
    }
    const out = Array.from(set);
    if (out.length) return out;
    const fallback = String(currentHazard?.risk_place_part || '').trim();
    return fallback ? [fallback] : [];
  }, [governAbnormalItems, currentHazard]);

  // governFilteredAbnormalItems: 由 watchedGovernPlacePart 驱动

  const canApproveClose = useMemo(() => {
    return !!(
      hasPermission?.('project_manager')
      || hasPermission?.('deputy_manager')
      || hasPermission?.('hazard_manage')
    );
  }, [hasPermission]);

  const parseRiskRecordFromQr = (text) => {
    if (!text) return null;
    let raw = String(text || '').trim();
    if (!raw) return null;

    // 尝试 URL decode（二维码内容常见为 URL 或 encodeURIComponent 结果）
    try {
      const decoded = decodeURIComponent(raw);
      if (decoded && typeof decoded === 'string') raw = decoded.trim() || raw;
    } catch (e) {
      // ignore
    }

    try {
      const obj = JSON.parse(raw);
      if (obj?.type === 'risk_record' && obj?.id != null) return { id: obj.id };
      // 兼容更多 payload
      if (obj?.risk_record_id != null) return { id: obj.risk_record_id };
      if (obj?.riskRecordId != null) return { id: obj.riskRecordId };
    } catch (e) {
      // ignore JSON parse errors
    }

    // 纯数字二维码
    if (/^\d+$/.test(raw)) return { id: Number(raw) };

    const match = raw.match(/risk[_-]?record\s*[:=]\s*(\d+)/i);
    if (match) return { id: Number(match[1]) };

    const idMatch = raw.match(/risk[_-]?record[_-]?id\s*[:=]\s*(\d+)/i);
    if (idMatch) return { id: Number(idMatch[1]) };

    const urlMatch = raw.match(/[?&]risk_record_id=(\d+)/i);
    if (urlMatch) return { id: Number(urlMatch[1]) };

    const urlMatch2 = raw.match(/[?&]riskRecordId=(\d+)/i);
    if (urlMatch2) return { id: Number(urlMatch2[1]) };

    // URL path 形式：.../risk-record/123 或 .../risk_record/123
    const pathMatch = raw.match(/risk[_-]?record\/(\d+)/i);
    if (pathMatch) return { id: Number(pathMatch[1]) };

    return null;
  };

  const openInspectionFromRiskRecord = async (recordId) => {
    const id = Number(recordId);
    if (!Number.isFinite(id)) return;

    // 注意：setRiskPoints 是异步的，扫码这里必须拿到 points 的即时返回值
    let points = (riskPoints || []);
    if (!points.length) {
      const loaded = await fetchRiskPoints();
      if (Array.isArray(loaded) && loaded.length) points = loaded;
    }

    const hit = (points || []).find(p => Number(p?.id) === id);
    if (!hit) {
      message.warning('二维码对应的风险记录不可用于隐患排查');
      return;
    }
    handleOpenModal(null, { scanOnly: true });
    setTimeout(() => {
      const measures = parseControlMeasuresToList(hit.control_measures);
      const checks = measures.map(m => ({
        measure: m,
        place_part: hit.risk_place_part || '',
        method: '',
        result: undefined,
        abnormal_description: undefined,
        rectification_responsible: undefined,
        rectification_deadline: null,
        status: 'PENDING'
      }));

      const safeRiskLevel = hit.risk_level || 'LOW';
      const safeHazardDesc =
        hit.risk_description
        || hit.hazard_description
        || (hit.risk_place_part ? `${hit.risk_place_part}隐患排查` : '隐患排查');

      // setFieldsValue 不会触发 onValuesChange，扫码场景要在这里同步写入衍生字段
      form.setFieldsValue({
        source_risk_record_id: id,
        risk_place_part: hit.risk_place_part,
        hazard_description: safeHazardDesc,
        risk_level: safeRiskLevel,
        control_measures: measures.join('\n'),
        control_measure_checks: checks,
        abnormal_description: undefined,
        rectification_responsible: undefined,
        rectification_deadline: null
      });
    }, 0);
  };

  const handleScanText = (text) => {
    const parsed = parseRiskRecordFromQr(text);
    if (!parsed?.id) {
      setScanError('未识别到有效的风险记录二维码');
      return;
    }
    setScanError('');
    stopScan();
    setScanModalVisible(false);
    openInspectionFromRiskRecord(parsed.id);
  };

  // Keep latest handler without re-triggering scan effects
  handleScanTextRef.current = handleScanText;

  useEffect(() => {
    if (!scanModalVisible) return;
    setScanSessionId(prev => prev + 1);
  }, [scanModalVisible]);

  useEffect(() => {
    if (!scanModalVisible) return;
    setScanError('');
    setScanDevicesLoading(true);

    // 不要在这里清空 selectedDeviceId，否则会导致切换时循环重置/闪烁
    stopScan();
    let active = true;

    const loadDevices = async () => {
      try {
        // 预热一次权限/设备（有些浏览器在未授予权限前拿不到 label）
        if (navigator?.mediaDevices?.getUserMedia) {
          try {
            const tmp = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            (tmp?.getTracks?.() || []).forEach(t => { try { t.stop?.(); } catch (e) { /* ignore */ } });
          } catch (e) {
            // ignore: decodeFromVideoDevice 会给出更明确的错误
          }
        }

        const devices = await BrowserQRCodeReader.listVideoInputDevices();
        if (!active) return;
        const list = devices || [];
        setScanDevices(list);

        const current = String(selectedDeviceId || '').trim();
        const stillExists = current && list.some(d => d.deviceId === current);
        const chosen = stillExists ? current : preferBackCameraDeviceId(list);
        setSelectedDeviceId(chosen || '');
      } catch (e) {
        if (!active) return;
        setScanError('无法获取摄像头设备列表，请检查浏览器权限');
      } finally {
        if (active) setScanDevicesLoading(false);
      }
    };

    loadDevices();

    return () => {
      active = false;
      setScanDevicesLoading(false);
      stopScan();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanModalVisible, stopScan, preferBackCameraDeviceId]);

  useEffect(() => {
    if (!scanModalVisible) return;
    const deviceId = String(selectedDeviceId || '').trim();
    if (!deviceId) return;

    let active = true;
    setScanError('');

    const startDecode = async () => {
      try {
        // 切换/重开时先彻底停掉，再给一点时间释放轨道
        stopScan();
        await sleep(180);

        const video = await waitForVideoEl();
        if (!active || !video) return;

        const reader = new BrowserQRCodeReader();
        scanReaderRef.current = reader;

        const controls = await reader.decodeFromVideoDevice(deviceId, video, (result) => {
          if (!active) return;
          if (result?.getText) handleScanTextRef.current?.(result.getText());
        });
        scanControlsRef.current = controls;
      } catch (e) {
        if (!active) return;
        setScanError('无法打开摄像头，请检查浏览器权限或是否被其它应用占用');
      }
    };

    startDecode();

    return () => {
      active = false;
      stopScan();
    };
  }, [scanModalVisible, selectedDeviceId, stopScan, sleep, waitForVideoEl]);

  const GOVERN_RESULT_OPTIONS = [
    { label: '已治理', value: '已治理' },
    { label: '部分治理', value: '部分治理' },
    { label: '无法治理', value: '无法治理' }
  ];

  const openGovernModal = (record, prefill = null) => {
    setCurrentHazard(record);
    governForm.resetFields();
    setGovernPhotoList([]);
    if (prefill && typeof prefill === 'object') {
      governForm.setFieldsValue(prefill);
    } else {
      const abnormalItems = abnormalCheckItems(record);
      const first = abnormalItems[0] || null;
      const placePart = String(first?.placePart || record?.risk_place_part || '').trim() || null;
      const abnormalItemKey = first ? buildAbnormalItemKey(first) : null;
      const abnormalMeasureText = first ? String(first?.measure || '').trim() : buildGovernAbnormalMeasureText(record);
      const abnormalDescText = first ? String(first?.abnormalDescription || '').trim() : buildGovernAbnormalDescriptionText(record);
      governForm.setFieldsValue({
        rectification_place_part: placePart,
        abnormal_item_key: abnormalItemKey || undefined,
        rectification_abnormal_measure: abnormalMeasureText || undefined,
        rectification_abnormal_description: abnormalDescText || undefined,
        rectification_responsible: record?.rectification_responsible || null
      });
    }
    setGovernModalVisible(true);
  };

  const openDetailModal = async (record) => {
    setDetailModalVisible(true);
    setDetailLoading(true);
    try {
      const res = await api.get(`/hazard/inspections/${record?.id}`);
      const data = res?.data?.data;
      if (data) {
        setDetailHazard(data);
      } else {
        setDetailHazard(record);
      }
    } catch (e) {
      setDetailHazard(record);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setDetailModalVisible(false);
    setDetailHazard(null);
  };

  const closeGovernModal = () => {
    setGovernModalVisible(false);
    setGovernPhotoList([]);
  };

  const submitGovernance = async (values) => {
    if (!currentHazard?.id) return;
    setGovernSubmitting(true);
    try {
      const abnormalKey = parseAbnormalItemKey(values?.abnormal_item_key);
      const abnormal_place_part_key = String(abnormalKey?.placePart || '').trim() || null;
      const abnormal_measure_key = String(abnormalKey?.measure || '').trim() || null;

      const rectification_actual_time = values?.rectification_actual_time
        ? dayjs(values.rectification_actual_time).format('YYYY-MM-DD HH:mm:ss')
        : dayjs().format('YYYY-MM-DD HH:mm:ss');

      const photoPayload = (governPhotoList || [])
        .filter(f => f && f.status === 'done')
        .map(f => ({
          objectName: f?.response?.objectName || f?.objectName || null,
          url: f?.url || null,
          fileName: f?.name || f?.fileName || null
        }))
        .filter(p => p.objectName || p.url);

      const contentLines = [
        values?.rectification_place_part ? `场所/部位：${values.rectification_place_part}` : null,
        values?.rectification_abnormal_measure ? `异常措施：${values.rectification_abnormal_measure}` : null,
        values?.rectification_abnormal_description ? `异常描述：${values.rectification_abnormal_description}` : null
        ,
        values?.rectification_action ? `治理措施：${values.rectification_action}` : null
      ].filter(Boolean);

      await api.post(`/hazard/inspections/${currentHazard.id}/rectifications`, {
        rectification_content: contentLines.join('\n') || '—',
        rectification_place_part: values?.rectification_place_part || null,
        rectification_abnormal_measure: values?.rectification_abnormal_measure || null,
        rectification_abnormal_description: values?.rectification_abnormal_description || null,
        abnormal_place_part_key,
        abnormal_measure_key,
        rectification_action: values?.rectification_action || null,
        rectification_result: values.rectification_result,
        rectification_responsible: values?.rectification_responsible || null,
        rectification_photos: photoPayload,
        rectification_actual_time,
        rectification_by: user?.name || user?.username || 'system',
        remark: values?.remark || null
      });

      message.success('治理记录已提交');
      setGovernModalVisible(false);
      setGovernPhotoList([]);
      fetchHazardList();
      fetchGovernList();
      fetchCloseList();
    } catch (e) {
      message.error(e?.response?.data?.error || '治理失败，请重试');
    } finally {
      setGovernSubmitting(false);
    }
  };

  // 倒计时计算
  const calculateCountdown = (deadline) => {
    const now = dayjs();
    const deadlineDate = dayjs(deadline);
    const diffDays = deadlineDate.diff(now, 'day');
    const diffHours = deadlineDate.diff(now, 'hour') % 24;
    
    if (diffDays < 0) {
      return <span style={{ color: '#ff4d4f' }}>已逾期 {Math.abs(diffDays)} 天</span>;
    }
    return <span style={{ color: diffDays <= 3 ? '#faad14' : '#52c41a' }}>
      剩余 {diffDays} 天 {diffHours} 小时
    </span>;
  };

  // 远程检索员工（用于整改责任人）
  const handleResponsibleSearch = useCallback(async (value) => {
    const q = String(value || '').trim();
    if (!q) {
      setResponsibleOptions([]);
      return;
    }
    setResponsibleLoading(true);
    try {
      const resp = await api.get(`/employees?name=${encodeURIComponent(q)}`);
      const list = Array.isArray(resp?.data) ? resp.data : [];
      const options = list.slice(0, 30).map(emp => {
        const labelParts = [];
        if (emp?.name) labelParts.push(emp.name);
        const dept = [emp?.department, emp?.section, emp?.team].filter(Boolean).join('-');
        if (dept) labelParts.push(dept);
        if (emp?.position) labelParts.push(emp.position);
        return {
          value: emp?.name || String(emp?.id || ''),
          label: labelParts.join(' / ') || (emp?.name || String(emp?.id || ''))
        };
      });
      setResponsibleOptions(options);
    } catch (e) {
      console.error('检索员工失败:', e);
      setResponsibleOptions([]);
    } finally {
      setResponsibleLoading(false);
    }
  }, []);

  // 获取隐患列表
  const fetchHazardList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        inspection_type: 'DORM',
        risk_level: selectedRiskLevel,
        status: selectedStatus,
        page: 1,
        page_size: 200
      };
      
      const response = await api.get('/hazard/inspections', { params });
      const payload = response?.data;
      if (payload?.success) {
        setHazardList(Array.isArray(payload.data) ? payload.data : []);
      } else if (Array.isArray(payload)) {
        // 兼容极少数情况下后端直接返回数组
        setHazardList(payload);
      } else {
        setHazardList([]);
      }
    } catch (error) {
      message.error('获取隐患列表失败');
      console.error('获取隐患列表失败:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedRiskLevel, selectedStatus]);

  // 获取“隐患风险记录”的风险点（用于新增隐患：针对风险点做排查）
  const fetchRiskPoints = useCallback(async () => {
    try {
      const resp = await api.get('/risk-management/hazard-records');
      const arr = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp?.data?.data) ? resp.data.data : []);
      const points = (arr || []).filter(r => r);
      setRiskPoints(points);
      return points;
    } catch (e) {
      // 不阻塞主流程
      console.error('获取风险场所/部位失败:', e);
      return [];
    }
  }, []);

  // 初始化数据
  useEffect(() => {
    fetchHazardList();
    fetchRiskPoints();
  }, [fetchHazardList, fetchRiskPoints]);

  const fetchHazardData = useCallback(async () => {
    setHazardDataLoading(true);
    try {
      const resp = await api.get('/hazard/hazard-data');
      const payload = resp?.data;
      if (payload?.success) {
        setHazardData(Array.isArray(payload.data) ? payload.data : []);
        setHazardDataSummary(payload.summary || null);
      } else {
        setHazardData([]);
        setHazardDataSummary(null);
      }
    } catch (e) {
      setHazardData([]);
      setHazardDataSummary(null);
      message.error('获取隐患数据失败');
    } finally {
      setHazardDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'data') return;
    fetchHazardData();
  }, [activeTab, fetchHazardData]);

  const fetchGovernList = useCallback(async () => {
    setGovernLoading(true);
    try {
      const response = await api.get('/hazard/inspections', {
        params: {
          inspection_type: 'DORM',
          status: 'PENDING',
          page: 1,
          page_size: 200
        }
      });
      const payload = response?.data;

      const filterOutAllNormalWithResultData = (list) => {
        const arr = Array.isArray(list) ? list : [];
        return arr.filter((record) => {
          const hasResultData = !!String(record?.inspection_result || '').trim();
          if (!hasResultData) return true;
          return abnormalCheckItems(record).length > 0;
        });
      };

      if (payload?.success) {
        setGovernList(filterOutAllNormalWithResultData(payload.data));
      } else if (Array.isArray(payload)) {
        setGovernList(filterOutAllNormalWithResultData(payload));
      } else {
        setGovernList([]);
      }
    } catch (e) {
      setGovernList([]);
      message.error('获取待治理隐患失败');
    } finally {
      setGovernLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'govern') return;
    fetchGovernList();
  }, [activeTab, fetchGovernList]);

  const fetchCloseList = useCallback(async () => {
    setCloseLoading(true);
    try {
      const response = await api.get('/hazard/inspections', {
        params: {
          inspection_type: 'DORM',
          page: 1,
          page_size: 200
        }
      });
      const payload = response?.data;
      if (payload?.success) {
        const list = Array.isArray(payload.data) ? payload.data : [];
        setCloseList(list.filter(r => {
          const st = String(r?.status || '').toUpperCase();
          return st === 'IN_PROGRESS' || st === 'COMPLETED';
        }));
      } else if (Array.isArray(payload)) {
        setCloseList(payload.filter(r => {
          const st = String(r?.status || '').toUpperCase();
          return st === 'IN_PROGRESS' || st === 'COMPLETED';
        }));
      } else {
        setCloseList([]);
      }
    } catch (e) {
      setCloseList([]);
      message.error('获取待闭环隐患失败');
    } finally {
      setCloseLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'close') return;
    fetchCloseList();
  }, [activeTab, fetchCloseList]);

  const openApproveModal = async (record) => {
    setApproveRecord(record || null);
    setApproveCandidates([]);
    approveForm.resetFields();
    setApproveModalVisible(true);
    if (!record?.id) return;
    try {
      const res = await api.get(`/hazard/inspections/${record.id}`);
      const data = res?.data?.data;
      if (data) setApproveRecord(data);
      const rects = Array.isArray(data?.rectifications) ? data.rectifications : [];
      const pending = rects
        .filter(r => !r?.verifier_id)
        .map(r => ({
          id: r.id,
          placePart: r?.abnormal_place_part_key || r?.rectification_place_part || '',
          measure: r?.abnormal_measure_key || r?.rectification_abnormal_measure || '',
          abnormalDescription: r?.rectification_abnormal_description || ''
        }));
      setApproveCandidates(pending);
      if (pending.length === 1) {
        approveForm.setFieldsValue({ rectification_id: pending[0].id });
      }
    } catch (e) {
      // ignore
    }
  };

  const closeApproveModal = () => {
    setApproveModalVisible(false);
    setApproveRecord(null);
    setApproveCandidates([]);
    approveForm.resetFields();
  };

  const submitApproveClose = async (values) => {
    if (!approveRecord?.id) return;
    const rectification_id = values?.rectification_id;
    if (!rectification_id) {
      message.error('请选择要审核的异常项');
      return;
    }
    const remark = String(values?.remark || '').trim();
    if (!remark) {
      message.error('请填写审批意见');
      return;
    }
    setApproveSubmitting(true);
    try {
      await api.post(`/hazard/inspections/${approveRecord.id}/approve-close`, { remark, rectification_id });
      message.success('审核闭环成功');
      closeApproveModal();
      fetchCloseList();
      fetchGovernList();
      fetchHazardList();
    } catch (e) {
      message.error(e?.response?.data?.error || '审核闭环失败');
    } finally {
      setApproveSubmitting(false);
    }
  };

  // 处理文件上传
  const handleFileChange = ({ fileList }) => {
    setUploadedFiles(fileList);
  };

  // 预览图片
  const handlePreviewImage = async (file) => {
    const url = file?.url || file?.thumbUrl || '';
    const type = String(file?.type || file?.originFileObj?.type || '').toLowerCase();

    // 非图片：直接新窗口打开（避免预览弹窗空白）
    if (url && !(type.startsWith('image/') || isProbablyImageUrl(url, file?.name))) {
      await openWithAuth(url);
      return;
    }

    if (url) {
      setPreviewImage(url);
      setImageModalVisible(true);
      return;
    }

    // 本地文件：生成 base64 预览
    if (file?.originFileObj) {
      try {
        const base64 = await getBase64(file.originFileObj);
        setPreviewImage(base64);
        setImageModalVisible(true);
      } catch (e) {
        message.error('预览失败');
      }
    }
  };

  // 治理结果照片上传（保存到 MinIO）
  const handleGovernPhotoChange = ({ fileList }) => {
    const normalized = (fileList || []).map(f => {
      const resp = f?.response;
      if (resp && typeof resp === 'object') {
        const url = resp?.url || resp?.fileUrl || f?.url;
        const objectName = resp?.objectName || resp?.object_name || f?.objectName;
        return {
          ...f,
          url: url || f?.url,
          objectName
        };
      }
      return f;
    });
    setGovernPhotoList(normalized);
  };

  const customUploadGovernPhoto = async (options) => {
    const { file, onSuccess, onError } = options;
    if (!currentHazard?.id) {
      onError?.(new Error('缺少隐患ID，无法上传'));
      return;
    }
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const resp = await api.post(`/hazard/inspections/${currentHazard.id}/rectification-photos`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const payload = resp?.data;
      const fileInfo = payload?.file || payload?.data || payload;
      onSuccess?.(fileInfo, file);
    } catch (e) {
      onError?.(e);
    }
  };

  // 处理表单提交
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      // scanOnlyMode 下某些字段是隐藏的（不一定会出现在 onFinish 的 values 里），这里从 form store 再兜底一次
      const resolvedHazardDesc = values?.hazard_description ?? form.getFieldValue('hazard_description');
      const resolvedRiskLevel = values?.risk_level ?? form.getFieldValue('risk_level');

      const formattedInspectionDate = values?.inspection_date
        ? dayjs(values.inspection_date).format('YYYY-MM-DD HH:mm:ss')
        : dayjs().format('YYYY-MM-DD HH:mm:ss');

      const controlChecks = Array.isArray(values?.control_measure_checks) ? values.control_measure_checks : [];

      const overallStatus = deriveOverallStatusFromChecks(controlChecks);
      const overallResponsible = deriveOverallRectificationResponsibleFromChecks(controlChecks);
      const overallDeadlineDayjs = deriveOverallRectificationDeadlineFromChecks(controlChecks);
      const overallDeadline = overallDeadlineDayjs ? overallDeadlineDayjs.format('YYYY-MM-DD') : null;

      const inspectionMeasuresJson = JSON.stringify({
        schema: 'control_measure_checks@2',
        items: controlChecks
          .map(i => ({
            measure: i?.measure || '',
            place_part: i?.place_part || '',
            method: i?.method || '',
            result: i?.result || null,
            abnormal_description: i?.abnormal_description || null,
            rectification_responsible: i?.rectification_responsible || null,
            rectification_deadline: i?.rectification_deadline
              ? (dayjs.isDayjs(i.rectification_deadline)
                  ? i.rectification_deadline.format('YYYY-MM-DD')
                  : (dayjs(i.rectification_deadline).isValid() ? dayjs(i.rectification_deadline).format('YYYY-MM-DD') : null))
              : null,
            status: i?.status || null
          }))
          .filter(i =>
            String(i.measure || '').trim() ||
            String(i.place_part || '').trim() ||
            String(i.method || '').trim() ||
            String(i.result || '').trim() ||
            String(i.abnormal_description || '').trim()
          )
      });
      const inspectionResultJson = JSON.stringify({
        schema: 'inspection_result_per_measure@1',
        results: controlChecks
          .map(i => ({
            measure: i?.measure || '',
            result: i?.result || null,
            abnormal_description: i?.abnormal_description || null
          }))
          .filter(i =>
            String(i.measure || '').trim() ||
            String(i.result || '').trim() ||
            String(i.abnormal_description || '').trim()
          )
      });

      const formData = {
        ...values,
        inspection_type: 'DORM',
        inspector_id: user?.id || 0,
        inspector_name: user?.name || user?.username || '未知',
        inspection_date: formattedInspectionDate,
        hazard_description: resolvedHazardDesc,
        risk_level: resolvedRiskLevel,
        // 兼容旧字段：从每条措施的整改信息汇总
        rectification_responsible: overallResponsible,
        rectification_deadline: overallDeadline,
        status: overallStatus,
        // 将“检查方法/结果”写入现有字段（后端无须变更即可存储）
        inspection_measures: inspectionMeasuresJson,
        inspection_result: inspectionResultJson
      };

      if (currentHazard) {
        // 更新隐患记录
        await api.put(`/hazard/inspections/${currentHazard.id}`, formData);

        const failedAttachments = [];

        // 编辑场景：把新增的本地文件上传到后端（已有附件不重复上传）
        if (uploadedFiles.length > 0) {
          for (const file of uploadedFiles) {
            if (file.originFileObj) {
              try {
                const uploadFormData = new FormData();
                uploadFormData.append('file', file.originFileObj);
                await api.post(`/hazard/inspections/${currentHazard.id}/attachments`, uploadFormData, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                });
              } catch (e) {
                const reason = e?.response?.data?.error || e?.message || '上传失败';
                failedAttachments.push({ name: file?.name || '未知文件', reason });
                console.error('上传附件失败:', e);
              }
            }
          }
        }

        if (failedAttachments.length > 0) {
          const first = failedAttachments[0];
          message.warning(
            `隐患记录更新成功，但有 ${failedAttachments.length} 个附件上传失败（例如：${first.name} - ${first.reason}）`
          );
        } else {
          message.success('隐患记录更新成功');
        }
      } else {
        // 创建新隐患记录
        const response = await api.post('/hazard/inspections', formData);
        const createdId = response?.data?.id ?? response?.data?.data?.id;

        if (!createdId) {
          throw new Error('创建隐患记录成功但未返回 id');
        }

        const failedAttachments = [];

        // 上传附件（不让附件失败影响主记录创建）
        if (uploadedFiles.length > 0) {
          for (const file of uploadedFiles) {
            if (file.originFileObj) {
              try {
                const uploadFormData = new FormData();
                uploadFormData.append('file', file.originFileObj);
                await api.post(`/hazard/inspections/${createdId}/attachments`, uploadFormData, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                });
              } catch (e) {
                const reason = e?.response?.data?.error || e?.message || '上传失败';
                failedAttachments.push({ name: file?.name || '未知文件', reason });
                console.error('上传附件失败:', e);
              }
            }
          }
        }

        if (failedAttachments.length > 0) {
          const first = failedAttachments[0];
          message.warning(
            `隐患记录创建成功，但有 ${failedAttachments.length} 个附件上传失败（例如：${first.name} - ${first.reason}）`
          );
        } else {
          message.success('隐患记录创建成功');
        }
      }
      
      // 重置表单和刷新列表
      handleReset();
      fetchHazardList();
    } catch (error) {
      const payload = error?.response?.data;
      const missing = Array.isArray(payload?.missing) ? payload.missing : null;
      if (missing && missing.length) {
        message.error(`缺少必要参数：${missing.join('、')}`);
      } else {
        message.error(payload?.error || '提交失败，请重试');
      }
      console.error('提交隐患记录失败:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setIsModalVisible(false);
    setCurrentHazard(null);
    form.resetFields();
    setUploadedFiles([]);
    setScanOnlyMode(false);
  };

  const handleOpenModal = async (hazard = null, options = {}) => {
    setCurrentHazard(hazard);
    setScanOnlyMode(!!options?.scanOnly);

    const applyHazardToForm = (hazardData) => {
      if (!hazardData) return;
      const measuresObj = parseJsonIfPossible(hazardData?.inspection_measures);
      const resultObj = parseJsonIfPossible(hazardData?.inspection_result);

      const resultMap = new Map();
      const abnormalMap = new Map();
      if (Array.isArray(resultObj?.results)) {
        for (const r of resultObj.results) {
          const key = String(r?.measure || '').trim();
          const val = r?.result || null;
          if (key) resultMap.set(key, val);
          const ab = r?.abnormal_description || null;
          if (key && ab) abnormalMap.set(key, ab);
        }
      }

      const controlMeasureChecks = Array.isArray(measuresObj?.items)
        ? measuresObj.items.map(i => ({
            measure: i?.measure || '',
            place_part: i?.place_part || i?.placePart || hazardData?.risk_place_part || '',
            method: i?.method || '',
            result: i?.result || resultMap.get(String(i?.measure || '').trim()) || undefined
            ,
            abnormal_description:
              i?.abnormal_description || abnormalMap.get(String(i?.measure || '').trim()) || undefined,
            rectification_responsible: i?.rectification_responsible || hazardData?.rectification_responsible || undefined,
            rectification_deadline: i?.rectification_deadline
              ? (dayjs(i.rectification_deadline).isValid() ? dayjs(i.rectification_deadline) : null)
              : (hazardData?.rectification_deadline && dayjs(hazardData.rectification_deadline).isValid() ? dayjs(hazardData.rectification_deadline) : null),
            status: i?.status || hazardData?.status || 'PENDING'
          }))
        : parseControlMeasuresToList(hazardData?.control_measures).map(m => ({
            measure: m,
            place_part: hazardData?.risk_place_part || '',
            method: '',
            result: resultMap.get(String(m).trim()) || undefined,
            abnormal_description: abnormalMap.get(String(m).trim()) || undefined,
            rectification_responsible: hazardData?.rectification_responsible || undefined,
            rectification_deadline: hazardData?.rectification_deadline && dayjs(hazardData.rectification_deadline).isValid()
              ? dayjs(hazardData.rectification_deadline)
              : null,
            status: hazardData?.status || 'PENDING'
          }));

      form.setFieldsValue({
        ...hazardData,
        rectification_deadline: hazardData?.rectification_deadline ? dayjs(hazardData.rectification_deadline) : null,
        inspection_date: hazardData?.inspection_date ? dayjs(hazardData.inspection_date) : dayjs(),
        control_measure_checks: controlMeasureChecks,
        // 旧字段不再使用（每条措施自带异常描述）
      });

      if (Array.isArray(hazardData?.attachments) && hazardData.attachments.length > 0) {
        const fileList = hazardData.attachments
          .map((attachment) => ({
            uid: attachment.id || `${attachment.file_name || 'att'}-${Math.random().toString(36).slice(2)}`,
            name: attachment.file_name || attachment.name || '附件',
            status: 'done',
            url: attachment.url || attachment.file_url || attachment.file_path || null
          }))
          .filter(f => f.url);
        setUploadedFiles(fileList);
      } else {
        setUploadedFiles([]);
      }
    };

    if (hazard) {
      // 编辑：优先按 id 拉详情，避免列表数据附件不同步/URL 过期导致不回显
      if (hazard?.id) {
        setIsModalVisible(true);
        setUploadedFiles([]);
        try {
          const res = await api.get(`/hazard/inspections/${hazard.id}`);
          const data = res?.data?.data || res?.data;
          if (data) {
            setCurrentHazard(data);
            applyHazardToForm(data);
            return;
          }
        } catch (e) {
          // ignore, fallback to passed-in hazard
        }
      }

      applyHazardToForm(hazard);
    } else {
      form.resetFields();
      setUploadedFiles([]);
      form.setFieldsValue({
        inspector_name: user?.name || user?.username || '未知',
        inspection_date: dayjs(),
        status: 'PENDING',
        risk_level: 'LOW'
      });
    }

    setIsModalVisible(true);
  };

  // 删除隐患记录
  const handleDelete = async (id) => {
    try {
      await api.delete(`/hazard/inspections/${id}`);
      message.success('隐患记录删除成功');
      fetchHazardList();
    } catch (error) {
      message.error('删除失败，请重试');
      console.error('删除隐患记录失败:', error);
    }
  };

  // 导出数据
  const handleExport = async () => {
    try {
      const params = {
        inspection_type: 'DORM',
        risk_level: selectedRiskLevel,
        status: selectedStatus
      };
      
      const response = await api.get('/hazard/export', { params });
      if (response.success) {
        // 这里可以使用文件下载库来处理导出
        message.success('数据导出成功');
        console.log('导出数据:', response.data);
      }
    } catch (error) {
      message.error('数据导出失败');
      console.error('导出数据失败:', error);
    }
  };

  // 打印功能
  const handlePrint = () => {
    window.print();
  };

  const getRiskColor = (level) => {
    if (level === 'HIGH') return token.colorError;
    if (level === 'MEDIUM') return token.colorWarning;
    return token.colorSuccess;
  };

  const getStatusTagColor = (status) => {
    if (status === 'COMPLETED') return 'green';
    if (status === 'IN_PROGRESS') return 'blue';
    return 'orange';
  };

  const getRiskShortText = (level) => {
    if (level === 'HIGH') return '高';
    if (level === 'MEDIUM') return '中';
    if (level === 'LOW') return '低';
    return '';
  };

  const renderBookmarkItem = (record, options = {}) => {
    const { actions } = options;
    const riskLevelLabel = (RISK_LEVELS.find(r => r.value === record?.risk_level) || {}).label || record?.risk_level || '-';
    const statusLabel = (STATUS_OPTIONS.find(s => s.value === record?.status) || {}).label || record?.status || '-';
    const deadlineText = record?.rectification_deadline ? dayjs(record.rectification_deadline).format('YYYY-MM-DD') : '-';
    const bookmarkColor = getRiskColor(record?.risk_level);
    const riskShortText = getRiskShortText(record?.risk_level);
    const actionNode = typeof actions === 'function' ? actions(record) : actions;

    return (
      <div
        onDoubleClick={() => openDetailModal(record)}
        style={{
          position: 'relative',
          border: `1px solid ${token.colorBorder}`,
          borderRadius: 8,
          padding: 10,
          paddingRight: 56,
          background: token.colorBgContainer,
          cursor: 'default'
        }}
      >
        {/* 右上角“标签书签” */}
        <div
          style={{
            position: 'absolute',
            top: -1,
            right: 12,
            width: 32,
            height: 44,
            background: bookmarkColor,
            clipPath: 'polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%)',
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            boxShadow: token.boxShadowSecondary,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: 6
          }}
        >
          <div
            style={{
              color: token.colorTextLightSolid,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: '14px',
              textAlign: 'center',
              width: 26
            }}
            title={riskLevelLabel}
          >
            {riskShortText}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {record?.risk_place_part || '-'}
            </div>
            <div style={{ color: 'rgba(0,0,0,0.65)', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {record?.hazard_description || '-'}
            </div>
            {record?.rectification_responsible ? (
              <div style={{ color: 'rgba(0,0,0,0.55)', fontSize: 12, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                整改责任人：{record?.rectification_responsible}
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 10 }}>
          <div style={{ color: 'rgba(0,0,0,0.65)', minWidth: 0 }}>
            整改时限：{deadlineText}
            {record?.rectification_deadline && record?.status !== 'COMPLETED' ? (
              <span style={{ marginLeft: 10, fontSize: 12 }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {calculateCountdown(record.rectification_deadline)}
              </span>
            ) : null}
          </div>
          <Tag color={getStatusTagColor(record?.status)} style={{ marginRight: 0 }}>
            {statusLabel}
          </Tag>
        </div>
        {actionNode ? (
          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
            {actionNode}
          </div>
        ) : null}
      </div>
    );
  };

  const lifecycleChartData = useMemo(() => {
    const s = hazardDataSummary || {};
    const items = [
      { type: '待排查', value: Number(s.to_inspect ?? 0) },
      { type: '已排查', value: Number(s.inspected ?? 0) },
      { type: '待治理', value: Number(s.to_govern ?? 0) },
      { type: '待闭环', value: Number(s.to_close ?? 0) }
    ];
    return items.filter(i => i.value > 0);
  }, [hazardDataSummary]);

  const riskLevelChartData = useMemo(() => {
    const counts = { HIGH: 0, MEDIUM: 0, LOW: 0 };
    for (const row of (hazardData || [])) {
      const lv = String(row?.risk_level || '').toUpperCase();
      if (lv === 'HIGH') counts.HIGH += 1;
      else if (lv === 'MEDIUM') counts.MEDIUM += 1;
      else if (lv === 'LOW') counts.LOW += 1;
    }
    const items = [
      { type: '高风险', value: counts.HIGH },
      { type: '中风险', value: counts.MEDIUM },
      { type: '低风险', value: counts.LOW }
    ];
    return items.filter(i => i.value > 0);
  }, [hazardData]);

  const lifecyclePieConfig = useMemo(() => {
    return {
      data: lifecycleChartData,
      angleField: 'value',
      colorField: 'type',
      radius: 0.9,
      innerRadius: 0.55,
      legend: { position: 'bottom' },
      label: {
        type: 'inner',
        offset: '-30%',
        content: ({ percent }) => `${Math.round((percent || 0) * 100)}%`,
        style: { textAlign: 'center' }
      },
      statistic: {
        title: false,
        content: {
          style: { whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis' },
          content: '状态分布'
        }
      }
    };
  }, [lifecycleChartData]);

  const riskLevelColumnConfig = useMemo(() => {
    return {
      data: riskLevelChartData,
      xField: 'type',
      yField: 'value',
      legend: false,
      label: {
        position: 'top',
        style: { opacity: 0.85 }
      },
      xAxis: { title: null },
      yAxis: { title: null, tickInterval: 1 }
    };
  }, [riskLevelChartData]);

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 12 : 0,
          marginBottom: isMobile ? 16 : 24
        }}
      >
        <h1 style={{ margin: 0, fontSize: isMobile ? 18 : 22, lineHeight: 1.2 }}>隐患管理</h1>
        <Space size="middle" direction={isMobile ? 'vertical' : 'horizontal'} style={isMobile ? { width: '100%' } : undefined}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal()}
            block={isMobile}
          >
            新增隐患排查
          </Button>
          <Button
            icon={<QrcodeOutlined />}
            onClick={() => setScanModalVisible(true)}
            block={isMobile}
          >
            扫一扫
          </Button>
          <Button
            icon={<ExportOutlined />}
            onClick={handleExport}
            block={isMobile}
          >
            导出数据
          </Button>
          <Button
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            block={isMobile}
          >
            打印
          </Button>
        </Space>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'data',
            label: '隐患数据',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card title="数据指标" bodyStyle={{ paddingTop: 12 }}>
                  {hazardDataSummary ? (
                    <>
                      <Row gutter={[16, 16]}>
                        <Col xs={12} sm={12} md={6}>
                          <Statistic title="待排查" value={hazardDataSummary.to_inspect ?? 0} />
                        </Col>
                        <Col xs={12} sm={12} md={6}>
                          <Statistic title="已排查" value={hazardDataSummary.inspected ?? 0} />
                        </Col>
                        <Col xs={12} sm={12} md={6}>
                          <Statistic title="待治理" value={hazardDataSummary.to_govern ?? 0} />
                        </Col>
                        <Col xs={12} sm={12} md={6}>
                          <Statistic title="待闭环" value={hazardDataSummary.to_close ?? 0} />
                        </Col>
                      </Row>

                      <Divider style={{ margin: '12px 0' }} />

                      <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                          <div style={{ fontWeight: 600, marginBottom: 8 }}>按状态归类</div>
                          <div style={{ height: 260 }}>
                            {lifecycleChartData.length ? <Pie {...lifecyclePieConfig} /> : <div style={{ color: 'rgba(0,0,0,0.45)' }}>暂无数据</div>}
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          <div style={{ fontWeight: 600, marginBottom: 8 }}>按风险等级归类</div>
                          <div style={{ height: 260 }}>
                            {riskLevelChartData.length ? <Column {...riskLevelColumnConfig} /> : <div style={{ color: 'rgba(0,0,0,0.45)' }}>暂无数据</div>}
                          </div>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <div style={{ color: 'rgba(0,0,0,0.45)' }}>暂无指标数据</div>
                  )}
                </Card>

                <Card>
                  <Table
                    rowKey={(r) => String(r?.risk_record_id)}
                    loading={hazardDataLoading}
                    dataSource={hazardData}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 860 } : undefined}
                    pagination={{ pageSize: 10, showSizeChanger: !isMobile, simple: isMobile, showTotal: (t) => `共 ${t} 条风险点` }}
                    columns={[
                      {
                        title: '风险场所/部位',
                        dataIndex: 'risk_place_part',
                        key: 'risk_place_part',
                        ellipsis: true
                      },
                      {
                        title: '风险描述',
                        dataIndex: 'risk_description',
                        key: 'risk_description',
                        ellipsis: true
                      },
                      {
                        title: '风险等级',
                        dataIndex: 'risk_level',
                        key: 'risk_level',
                        width: 100,
                        render: (v) => {
                          const s = String(v || '').toUpperCase();
                          if (!s) return '—';
                          if (s === 'HIGH') return <Tag color="red">高风险</Tag>;
                          if (s === 'MEDIUM') return <Tag color="orange">中风险</Tag>;
                          return <Tag color="green">低风险</Tag>;
                        }
                      },
                      {
                        title: '状态',
                        dataIndex: 'lifecycle',
                        key: 'lifecycle',
                        width: 100,
                        render: (v, row) => {
                          const code = row?.lifecycle_code;
                          if (code === 'TO_GOVERN') return <Tag color="red">待治理</Tag>;
                          if (code === 'TO_CLOSE') return <Tag color="orange">待闭环</Tag>;
                          if (code === 'INSPECTED') return <Tag color="green">已排查</Tag>;
                          return <Tag>待排查</Tag>;
                        }
                      },
                      {
                        title: '最近排查时间',
                        key: 'inspection_date',
                        width: 160,
                        render: (_, row) => {
                          const dt = row?.last_inspection?.inspection_date;
                          if (!dt) return '—';
                          return dayjs(dt).isValid() ? dayjs(dt).format('YYYY-MM-DD HH:mm') : String(dt);
                        }
                      },
                      {
                        title: '整改责任人',
                        key: 'rectification_responsible',
                        width: 120,
                        render: (_, row) => row?.last_inspection?.rectification_responsible || '—'
                      },
                      {
                        title: '整改期限',
                        key: 'rectification_deadline',
                        width: 120,
                        render: (_, row) => {
                          const d = row?.last_inspection?.rectification_deadline;
                          if (!d) return '—';
                          return dayjs(d).isValid() ? dayjs(d).format('YYYY-MM-DD') : String(d);
                        }
                      },
                      {
                        title: '操作',
                        key: 'action',
                        width: 100,
                        render: (_, row) => {
                          const id = row?.last_inspection?.id;
                          if (!id) return '—';
                          return (
                            <Button size="small" onClick={() => openDetailModal({ id })}>
                              查看
                            </Button>
                          );
                        }
                      }
                    ]}
                  />
                </Card>
              </Space>
            )
          },
          {
            key: 'inspection',
            label: '隐患清单',
            children: (
              <>
                {/* 筛选区域 */}
                <Card style={{ marginBottom: 24 }}>
                  <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={8}>
                      <Input
                        placeholder="搜索风险场所/部位、隐患描述等"
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Select
                        placeholder="选择风险等级"
                        value={selectedRiskLevel}
                        onChange={setSelectedRiskLevel}
                        style={{ width: '100%' }}
                      >
                        <Option value="">全部</Option>
                        {RISK_LEVELS.map(level => (
                          <Option key={level.value} value={level.value}>{level.label}</Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Select
                        placeholder="选择状态"
                        value={selectedStatus}
                        onChange={setSelectedStatus}
                        style={{ width: '100%' }}
                      >
                        <Option value="">全部</Option>
                        {STATUS_OPTIONS.map(status => (
                          <Option key={status.value} value={status.value}>{status.label}</Option>
                        ))}
                      </Select>
                    </Col>
                  </Row>
                </Card>

                {/* 隐患列表 */}
                <Card>
                  <List
                    loading={loading}
                    dataSource={hazardList}
                    grid={{ gutter: isMobile ? 12 : 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
                    pagination={{
                      pageSize: 12,
                      showSizeChanger: !isMobile,
                      simple: isMobile,
                      showTotal: (total) => `共 ${total} 条记录`
                    }}
                    renderItem={(item) => (
                      <List.Item>
                        {renderBookmarkItem(item)}
                      </List.Item>
                    )}
                  />
                </Card>
              </>
            )
          },
          {
            key: 'govern',
            label: '隐患治理',
            children: (
              <Card>
                <List
                  loading={governLoading}
                  dataSource={governList}
                  grid={{ gutter: isMobile ? 12 : 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
                  pagination={{
                    pageSize: 12,
                    showSizeChanger: !isMobile,
                    simple: isMobile,
                    showTotal: (total) => `共 ${total} 条待治理隐患`
                  }}
                  renderItem={(item) => (
                    <List.Item key={String(item?.id)}>
                      {renderBookmarkItem(item, {
                        actions: () => (
                          <Space
                            size={8}
                            wrap
                            direction={isMobile ? 'vertical' : 'horizontal'}
                            style={isMobile ? { width: '100%' } : undefined}
                          >
                            <Button size="small" onClick={() => openDetailModal(item)} block={isMobile}>
                              查看
                            </Button>
                            <Button size="small" type="primary" onClick={() => openGovernModal(item)} block={isMobile}>
                              治理
                            </Button>
                          </Space>
                        )
                      })}
                    </List.Item>
                  )}
                />
              </Card>
            )
          },
          {
            key: 'close',
            label: '隐患闭环',
            children: (
              <Card>
                <List
                  loading={closeLoading}
                  dataSource={closeList}
                  grid={{ gutter: isMobile ? 12 : 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
                  pagination={{
                    pageSize: 12,
                    showSizeChanger: !isMobile,
                    simple: isMobile,
                    showTotal: (total) => `共 ${total} 条隐患（待闭环/已完成）`
                  }}
                  renderItem={(item) => (
                    <List.Item key={String(item?.id)}>
                      {renderBookmarkItem(item, {
                        actions: () => (
                          <Space
                            size={8}
                            wrap
                            direction={isMobile ? 'vertical' : 'horizontal'}
                            style={isMobile ? { width: '100%' } : undefined}
                          >
                            <Button size="small" onClick={() => openDetailModal(item)} block={isMobile}>
                              查看
                            </Button>
                            {String(item?.status || '').toUpperCase() === 'COMPLETED' ? (
                              <Tag color="green">已完成</Tag>
                            ) : canApproveClose ? (
                              <Button size="small" type="primary" onClick={() => openApproveModal(item)} block={isMobile}>
                                审核闭环
                              </Button>
                            ) : (
                              <Tag>等待审核</Tag>
                            )}
                          </Space>
                        )
                      })}
                    </List.Item>
                  )}
                />

                <Modal
                  title="闭环审批"
                  open={approveModalVisible}
                  onCancel={closeApproveModal}
                  okText="提交"
                  cancelText="取消"
                  confirmLoading={approveSubmitting}
                  onOk={() => approveForm.submit()}
                  destroyOnClose
                >
                  <Form
                    form={approveForm}
                    layout="vertical"
                    onFinish={submitApproveClose}
                    preserve={false}
                  >
                    <Form.Item
                      label="待审核异常项"
                      name="rectification_id"
                      rules={[{ required: true, message: '请选择要审核的异常项' }]}
                    >
                      <Select
                        placeholder={approveCandidates.length ? '请选择异常项' : '暂无待审核异常项'}
                        disabled={!approveCandidates.length}
                        options={(approveCandidates || []).map(it => {
                          const place = String(it?.placePart || '').trim();
                          const measure = String(it?.measure || '').trim();
                          const desc = String(it?.abnormalDescription || '').trim();
                          return {
                            value: it.id,
                            label: `${place ? place + ' - ' : ''}${measure || '—'}${desc ? '（' + desc + '）' : ''}`
                          };
                        })}
                        showSearch
                        optionFilterProp="label"
                      />
                    </Form.Item>
                    <Form.Item
                      label="审批意见"
                      name="remark"
                      rules={[{ required: true, whitespace: true, message: '请填写审批意见' }]}
                    >
                      <TextArea rows={4} placeholder="请输入审批意见" />
                    </Form.Item>
                  </Form>
                </Modal>
              </Card>
            )
          },
        ]}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={currentHazard ? '编辑隐患记录' : '新增隐患排查'}
        open={isModalVisible}
        onCancel={handleReset}
        footer={null}
        width={isMobile ? '100%' : 800}
        style={isMobile ? { top: 16 } : undefined}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={(changed) => {
            const ch = changed || {};

            if ('source_risk_record_id' in ch) {
              const id = ch.source_risk_record_id;
              const selected = (riskPoints || []).find(p => String(p.id) === String(id));
              if (!selected) {
                form.setFieldsValue({
                  risk_place_part: undefined,
                  hazard_description: undefined,
                  risk_level: 'LOW',
                  control_measures: undefined,
                  control_measure_checks: [],
                  abnormal_description: undefined,
                  rectification_responsible: undefined,
                  rectification_deadline: null
                });
                return;
              }
              const measures = parseControlMeasuresToList(selected.control_measures);
              const checks = measures.map(m => ({
                measure: m,
                place_part: selected.risk_place_part || '',
                method: '',
                result: undefined,
                abnormal_description: undefined,
                rectification_responsible: undefined,
                rectification_deadline: null,
                status: 'PENDING'
              }));

              // 后端创建隐患时 hazard_description / risk_level 是必填：避免风险点字段为空把默认值覆盖成 null
              const safeRiskLevel = selected.risk_level || 'LOW';
              const safeHazardDesc =
                selected.risk_description
                || selected.hazard_description
                || (selected.risk_place_part ? `${selected.risk_place_part}隐患排查` : '隐患排查');

              form.setFieldsValue({
                risk_place_part: selected.risk_place_part,
                hazard_description: safeHazardDesc,
                risk_level: safeRiskLevel,
                control_measures: measures.join('\n'),
                control_measure_checks: checks,
                abnormal_description: undefined,
                rectification_responsible: undefined,
                rectification_deadline: null
              });
            }
          }}
          initialValues={{
            risk_level: 'LOW',
            status: 'PENDING',
            inspection_date: dayjs()
          }}
        >
          <Row gutter={[16, 16]}>
            {scanOnlyMode ? (
              <Col xs={24}>
                <Form.Item
                  name="source_risk_record_id"
                  rules={[{ required: true, message: '请选择风险点' }]}
                  hidden
                >
                  <Input />
                </Form.Item>

                {/* 扫码模式下这些字段不展示，但后端创建隐患需要 hazard_description/risk_level，必须注册进表单 */}
                <Form.Item name="hazard_description" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="risk_level" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="risk_place_part" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="control_measures" hidden>
                  <Input />
                </Form.Item>
              </Col>
            ) : (
              <>
                <Col xs={24}>
                  <Divider orientation="left" style={{ margin: '4px 0 12px' }}>风险点信息</Divider>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    name="source_risk_record_id"
                    label="风险点（来自隐患风险记录）"
                    rules={[{ required: true, message: '请选择风险点' }]}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="请选择风险点（场所/部位 + 风险描述）"
                      optionFilterProp="label"
                      options={(riskPoints || []).map(p => ({
                        value: p.id,
                        label: `${p.risk_place_part || ''}${p.risk_place_part ? ' - ' : ''}${p.risk_description || ''}`
                      }))}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    name="risk_place_part"
                    label="风险所在场所 / 部位"
                  >
                    <Input disabled placeholder="选择风险点后自动带出" />
                  </Form.Item>
                </Col>

                {/* 隐患详情 */}
                <Col xs={24}>
                  <Form.Item
                    name="hazard_description"
                    label="风险描述（来自风险点）"
                    rules={[{ required: true, message: '请输入隐患描述' }]}
                  >
                    <TextArea rows={3} disabled placeholder="选择风险点后自动带出" />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item name="control_measures" label="管控措施">
                    <TextArea rows={3} disabled placeholder="选择风险点后自动带出" />
                  </Form.Item>
                </Col>
              </>
            )}

            <Col xs={24}>
              <Divider orientation="left" style={{ margin: '4px 0 12px' }}>检查与整改（按管控措施）</Divider>
            </Col>

            <Col xs={24}>
              <Form.Item
                label="检查方法 / 检查结果（针对管控措施）"
                required
              >
                <Form.List name="control_measure_checks">
                  {(fields) => (
                    <div>
                      {fields.length === 0 ? (
                        <div style={{ color: 'rgba(0,0,0,0.45)' }}>请选择风险点以加载管控措施</div>
                      ) : (
                        fields.map((field, idx) => {
                          const arr = form.getFieldValue('control_measure_checks') || [];
                          const measure = String(arr?.[field.name]?.measure || '').trim();
                          return (
                            <Card
                              key={field.key}
                              size="small"
                              title={`管控措施 ${idx + 1}${measure ? '：' + measure : ''}`}
                              style={{ marginBottom: 12 }}
                              bodyStyle={{ padding: 12 }}
                            >
                            <Form.Item {...field} name={[field.name, 'measure']} hidden>
                              <Input />
                            </Form.Item>

                            <Row gutter={12} style={{ marginBottom: 8 }}>
                              <Col xs={24} md={8}>
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'place_part']}
                                  label="场所/部位"
                                  style={{ marginBottom: 0 }}
                                  rules={[{ required: true, whitespace: true, message: '请填写场所/部位' }]}
                                >
                                  <Input placeholder="场所/部位" />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={10}>
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'method']}
                                  label="检查方法"
                                  style={{ marginBottom: 0 }}
                                  rules={[{ required: true, whitespace: true, message: '请输入检查方法' }]}
                                >
                                  <Input placeholder="请输入检查方法" />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={6}>
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'result']}
                                  label="检查结果"
                                  style={{ marginBottom: 0 }}
                                  rules={[{ required: true, message: '请选择检查结果' }]}
                                >
                                  <Select placeholder="检查结果" options={MEASURE_RESULT_OPTIONS} />
                                </Form.Item>
                              </Col>
                            </Row>

                            <Row gutter={12} style={{ marginBottom: 8 }}>
                              <Col xs={24} md={8}>
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'rectification_responsible']}
                                  label="整改责任人"
                                  style={{ marginBottom: 0 }}
                                >
                                  <Select
                                    placeholder="输入姓名检索员工"
                                    showSearch
                                    filterOption={false}
                                    onSearch={handleResponsibleSearch}
                                    loading={responsibleLoading}
                                    options={responsibleOptions}
                                    notFoundContent={responsibleLoading ? '检索中...' : '请输入姓名关键字检索'}
                                    allowClear
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </Col>

                              <Col xs={24} md={8}>
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'rectification_deadline']}
                                  label="整改时限"
                                  style={{ marginBottom: 0 }}
                                >
                                  <DatePicker
                                    style={{ width: '100%' }}
                                    placeholder="请选择整改时限"
                                    minDate={dayjs()}
                                  />
                                </Form.Item>
                              </Col>

                              <Col xs={24} md={8}>
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'status']}
                                  label="状态"
                                  style={{ marginBottom: 0 }}
                                >
                                  <Select placeholder="请选择状态">
                                    {STATUS_OPTIONS.map(status => (
                                      <Option key={status.value} value={status.value}>{status.label}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                            </Row>

                            <Form.Item noStyle shouldUpdate={(prev, cur) => {
                              const p = (prev?.control_measure_checks || [])[field.name]?.result;
                              const n = (cur?.control_measure_checks || [])[field.name]?.result;
                              return p !== n;
                            }}>
                              {() => {
                                const arr = form.getFieldValue('control_measure_checks') || [];
                                const r = arr?.[field.name]?.result;
                                const show = r === 'abnormal';
                                return show ? (
                                  <Row gutter={12}>
                                    <Col xs={24}>
                                      <Form.Item
                                        name={[field.name, 'abnormal_description']}
                                        label="异常描述"
                                        rules={[{ required: true, whitespace: true, message: '请填写该措施对应的异常描述' }]}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <TextArea rows={2} placeholder="请描述该措施对应的异常隐患情况" />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                ) : null;
                              }}
                            </Form.Item>
                          </Card>
                          );
                        })
                      )}
                    </div>
                  )}
                </Form.List>
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Divider orientation="left" style={{ margin: '4px 0 12px' }}>排查信息</Divider>
            </Col>

            {!scanOnlyMode && (
              <>
                {/* 隐患分级 */}
                <Col xs={24} md={8}>
                  <Form.Item
                    name="risk_level"
                    label="隐患等级"
                    rules={[{ required: true, message: '请选择风险等级' }]}
                  >
                    <Select placeholder="请选择隐患等级">
                      {RISK_LEVELS.map(level => (
                        <Option key={level.value} value={level.value}>{level.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {/* 排查信息 */}
                <Col xs={24} md={8}>
                  <Form.Item
                    name="inspector_name"
                    label="排查人"
                    rules={[{ required: true, message: '请输入排查人姓名' }]}
                  >
                    <Input placeholder="请输入排查人姓名" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="inspection_date"
                    label="排查日期"
                    rules={[{ required: true, message: '请选择排查日期' }]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      placeholder="请选择排查日期"
                      showTime
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Divider orientation="left" style={{ margin: '4px 0 12px' }}>备注与附件</Divider>
                </Col>

                {/* 备注 */}
                <Col xs={24}>
                  <Form.Item
                    name="remark"
                    label="备注"
                  >
                    <TextArea rows={3} placeholder="请输入备注信息" />
                  </Form.Item>
                </Col>

                {/* 图片上传 */}
                <Col xs={24}>
                  <Form.Item
                    name="attachments"
                    label="现场照片"
                  >
                    <Upload
                      listType="picture-card"
                      accept="image/*"
                      fileList={uploadedFiles}
                      onChange={handleFileChange}
                      beforeUpload={() => false} // 手动上传
                      onPreview={handlePreviewImage}
                    >
                      <div>
                        <UploadOutlined />
                        <div style={{ marginTop: 6 }}>上传</div>
                      </div>
                    </Upload>
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <Space size="middle">
              <Button onClick={handleReset}>取消</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {submitting ? '提交中...' : '提交'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* 隐患治理 */}
      <Modal
        title="隐患治理"
        open={governModalVisible}
        onCancel={closeGovernModal}
        footer={null}
        width={isMobile ? '100%' : 700}
        style={isMobile ? { top: 16 } : undefined}
        destroyOnClose
      >
        <Form
          form={governForm}
          layout="vertical"
          onFinish={submitGovernance}
          onValuesChange={(changedValues) => {
            // 场所/部位变更：自动切换到该场所下的第一个异常项（若存在）
            if (Object.prototype.hasOwnProperty.call(changedValues || {}, 'rectification_place_part')) {
              const first = (governFilteredAbnormalItems || [])[0] || null;
              if (first) {
                const key = buildAbnormalItemKey(first);
                governForm.setFieldsValue({
                  abnormal_item_key: key,
                  rectification_abnormal_measure: String(first?.measure || '').trim() || undefined,
                  rectification_abnormal_description: String(first?.abnormalDescription || '').trim() || undefined
                });
              }
              return;
            }

            // 异常项变更：回填异常措施/描述
            if (Object.prototype.hasOwnProperty.call(changedValues || {}, 'abnormal_item_key')) {
              const { placePart, measure } = parseAbnormalItemKey(changedValues?.abnormal_item_key);
              const hit = (governAbnormalItems || []).find(it =>
                String(it?.measure || '').trim() === String(measure || '').trim() &&
                String(it?.placePart || '').trim() === String(placePart || '').trim()
              );
              if (hit) {
                governForm.setFieldsValue({
                  rectification_place_part: String(hit?.placePart || '').trim() || governForm.getFieldValue('rectification_place_part') || undefined,
                  rectification_abnormal_measure: String(hit?.measure || '').trim() || undefined,
                  rectification_abnormal_description: String(hit?.abnormalDescription || '').trim() || undefined
                });
              }
            }
          }}
        >
          <Form.Item
            name="rectification_place_part"
            label="场所 / 部位"
            rules={[{ required: true, whitespace: true, message: '请填写场所/部位' }]}
          >
            <Select
              placeholder="请选择或输入场所/部位"
              mode="tags"
              options={(governPlacePartOptions || []).map(p => ({ value: p, label: p }))}
            />
          </Form.Item>

          <Form.Item
            name="abnormal_item_key"
            label="异常项"
            rules={[{ required: true, message: '请选择异常项' }]}
          >
            <Select
              placeholder="请选择异常项（按场所/部位过滤）"
              options={(governFilteredAbnormalItems || []).map(it => {
                const place = String(it?.placePart || '').trim();
                const measure = String(it?.measure || '').trim();
                const desc = String(it?.abnormalDescription || '').trim();
                return {
                  value: buildAbnormalItemKey(it),
                  label: `${place ? place + ' - ' : ''}${measure || '—'}${desc ? '（' + desc + '）' : ''}`
                };
              })}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            name="rectification_abnormal_measure"
            label="异常措施"
            rules={[{ required: true, whitespace: true, message: '请填写异常措施' }]}
          >
            <TextArea rows={2} placeholder="请输入异常措施" />
          </Form.Item>

          <Form.Item
            name="rectification_abnormal_description"
            label="异常描述"
            rules={[{ required: true, whitespace: true, message: '请填写异常描述' }]}
          >
            <TextArea rows={3} placeholder="请输入异常描述" />
          </Form.Item>

          <Form.Item
            name="rectification_action"
            label="治理措施"
            rules={[{ required: true, whitespace: true, message: '请填写治理措施' }]}
          >
            <TextArea rows={3} placeholder="请输入实际采取的治理措施" />
          </Form.Item>

          <Form.Item
            name="rectification_result"
            label="治理结果"
            rules={[{ required: true, message: '请选择治理结果' }]}
          >
            <Select placeholder="请选择治理结果" options={GOVERN_RESULT_OPTIONS} />
          </Form.Item>

          <Form.Item
            name="rectification_responsible"
            label="治理责任人"
            rules={[{ required: true, message: '请选择治理责任人' }]}
          >
            <Select
              placeholder="输入姓名检索员工"
              showSearch
              filterOption={false}
              onSearch={handleResponsibleSearch}
              loading={responsibleLoading}
              options={responsibleOptions}
              notFoundContent={responsibleLoading ? '检索中...' : '请输入姓名关键字检索'}
              allowClear
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item label="治理结果照片">
            <Upload
              listType="picture-card"
              accept="image/*"
              fileList={governPhotoList}
              onChange={handleGovernPhotoChange}
              onPreview={handlePreviewImage}
              customRequest={customUploadGovernPhoto}
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 6 }}>上传</div>
              </div>
            </Upload>
          </Form.Item>

          <Form.Item name="rectification_actual_time" label="治理完成时间">
            <DatePicker showTime style={{ width: '100%' }} placeholder="请选择治理完成时间" />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input placeholder="可选" />
          </Form.Item>

          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={closeGovernModal}>取消</Button>
            <Button type="primary" htmlType="submit" loading={governSubmitting}>
              提交治理
            </Button>
          </Space>
        </Form>
      </Modal>

      {/* 详情查看（双击书签打开） */}
      <Modal
        title="隐患详情"
        open={detailModalVisible}
        onCancel={closeDetailModal}
        footer={
          <Space direction={isMobile ? 'vertical' : 'horizontal'} wrap style={isMobile ? { width: '100%' } : undefined}>
            <Button onClick={closeDetailModal} block={isMobile}>关闭</Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                if (detailHazard) handleOpenModal(detailHazard);
                closeDetailModal();
              }}
              block={isMobile}
            >
              编辑
            </Button>
            <Popconfirm
              title="确定要删除这条记录吗？"
              onConfirm={async () => {
                if (!detailHazard?.id) return;
                await handleDelete(detailHazard.id);
                closeDetailModal();
              }}
              okText="确定"
              cancelText="取消"
            >
              <Button danger icon={<DeleteOutlined />} block={isMobile}>
                删除
              </Button>
            </Popconfirm>
          </Space>
        }
        width={isMobile ? '100%' : 820}
        style={isMobile ? { top: 16 } : undefined}
        destroyOnClose
      >
        {detailLoading && (
          <div style={{ padding: '8px 0', color: 'rgba(0,0,0,0.45)' }}>正在加载详情...</div>
        )}

        <Descriptions column={isMobile ? 1 : 2} size="small" bordered>
          <Descriptions.Item label="风险所在场所 / 部位" span={2}>
            {detailHazard?.risk_place_part || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="风险描述" span={2}>
            {detailHazard?.hazard_description || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="风险等级">
            {detailHazard?.risk_level || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            {detailHazard?.status || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="整改时限">
            {detailHazard?.rectification_deadline ? dayjs(detailHazard.rectification_deadline).format('YYYY-MM-DD') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="整改责任人">
            {detailHazard?.rectification_responsible || '-'}
          </Descriptions.Item>

          <Descriptions.Item label="检查方法" span={2}>
            {(() => {
              const measuresObj = parseJsonIfPossible(detailHazard?.inspection_measures);
              const items = Array.isArray(measuresObj?.items) ? measuresObj.items : null;
              const methodsText = items
                ? items
                    .map(i => {
                      const m = String(i?.measure || '').trim();
                      const method = String(i?.method || '').trim();
                      if (!m && !method) return '';
                      return m ? `${m}：${method || '—'}` : (method || '—');
                    })
                    .filter(Boolean)
                    .join('\n')
                : null;

              return (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                  {methodsText || detailHazard?.inspection_measures || '-'}
                </pre>
              );
            })()}
          </Descriptions.Item>

          <Descriptions.Item label="检查结果" span={2}>
            {(() => {
              const resultObj = parseJsonIfPossible(detailHazard?.inspection_result);
              const labelMap = new Map(MEASURE_RESULT_OPTIONS.map(o => [o.value, o.label]));

              // 新结构：每条措施一个结果
              if (Array.isArray(resultObj?.results) && resultObj.results.length > 0) {
                const lines = resultObj.results
                  .map(r => {
                    const m = String(r?.measure || '').trim();
                    const v = r?.result;
                    const t = labelMap.get(v) || (v ? String(v) : '—');
                    if (!m && !t) return '';
                    return m ? `${m}：${t}` : t;
                  })
                  .filter(Boolean)
                  .join('\n');

                return (
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {lines || '-'}
                  </pre>
                );
              }

              // 旧结构：checks 数组
              const checks = Array.isArray(resultObj?.checks) ? resultObj.checks : null;
              if (checks) {
                return checks.map(v => labelMap.get(v) || v).join('、') || '-';
              }

              return detailHazard?.inspection_result || '-';
            })()}
          </Descriptions.Item>

          {(() => {
            const resultObj = parseJsonIfPossible(detailHazard?.inspection_result);
            const measuresObj = parseJsonIfPossible(detailHazard?.inspection_measures);
            const items = Array.isArray(measuresObj?.items) ? measuresObj.items : [];
            const byMeasure = new Map();
            for (const it of items) {
              const m = String(it?.measure || '').trim();
              const ab = String(it?.abnormal_description || '').trim();
              if (m && ab) byMeasure.set(m, ab);
            }
            if (Array.isArray(resultObj?.results)) {
              for (const r of resultObj.results) {
                const m = String(r?.measure || '').trim();
                const ab = String(r?.abnormal_description || '').trim();
                if (m && ab) byMeasure.set(m, ab);
              }
            }
            const lines = Array.from(byMeasure.entries())
              .map(([m, ab]) => `${m}：${ab}`)
              .join('\n');
            return lines ? (
              <Descriptions.Item label="隐患描述（异常）" span={2}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{lines}</pre>
              </Descriptions.Item>
            ) : null;
          })()}

          <Descriptions.Item label="排查人">
            {detailHazard?.inspector_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="排查日期">
            {detailHazard?.inspection_date ? dayjs(detailHazard.inspection_date).format('YYYY-MM-DD HH:mm') : '-'}
          </Descriptions.Item>
        </Descriptions>

        {/* 隐患附件/图片 */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>隐患图片/附件</div>
          {(() => {
            const atts = Array.isArray(detailHazard?.attachments) ? detailHazard.attachments : [];
            const normalized = atts
              .map(a => {
                const name = a?.file_name || a?.fileName || a?.name || '附件';
                const mime = a?.file_type || a?.mime_type || a?.type || '';
                const url = a?.url || a?.file_url || a?.file_path || a?.fileUrl || null;
                return { id: a?.id || name, name, mime, url };
              })
              .filter(a => a.url);

            if (normalized.length === 0) {
              return <div style={{ color: 'rgba(0,0,0,0.45)' }}>暂无附件</div>;
            }

            const images = normalized.filter(a => String(a.mime || '').toLowerCase().startsWith('image/'));
            const others = normalized.filter(a => !String(a.mime || '').toLowerCase().startsWith('image/'));

            return (
              <>
                {images.length > 0 && (
                  <Space size={8} wrap>
                    {images.map((p, idx) => (
                      <AuthedImage
                        key={`${p.id}-${idx}`}
                        src={p.url}
                        alt={p.name}
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: `1px solid ${token.colorBorder}` }}
                        onClick={() => handlePreviewImage({ url: p.url })}
                      />
                    ))}
                  </Space>
                )}
                {others.length > 0 && (
                  <div style={{ marginTop: images.length > 0 ? 8 : 0 }}>
                    {others.map((f, idx) => (
                      <div key={`${f.id}-${idx}`} style={{ marginTop: 4 }}>
                        <button
                          type="button"
                          onClick={() => openWithAuth(f.url)}
                          style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: '#1677ff' }}
                        >
                          {f.name}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>治理记录</div>
          {Array.isArray(detailHazard?.rectifications) && detailHazard.rectifications.length > 0 ? (
            <List
              size="small"
              bordered
              dataSource={detailHazard.rectifications.slice().sort((a, b) => {
                const ta = a?.rectification_actual_time ? dayjs(a.rectification_actual_time).valueOf() : 0;
                const tb = b?.rectification_actual_time ? dayjs(b.rectification_actual_time).valueOf() : 0;
                return tb - ta;
              })}
              renderItem={(item) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ fontWeight: 600 }}>
                        {item?.rectification_responsible || item?.rectification_by || '—'}
                      </div>
                      <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                        {item?.rectification_actual_time ? dayjs(item.rectification_actual_time).format('YYYY-MM-DD HH:mm') : '-'}
                      </div>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <div><span style={{ color: 'rgba(0,0,0,0.45)' }}>治理内容：</span>{item?.rectification_content || '-'}</div>
                      <div style={{ marginTop: 4 }}><span style={{ color: 'rgba(0,0,0,0.45)' }}>治理结果：</span>{item?.rectification_result || '-'}</div>
                      {Array.isArray(item?.rectification_photos) && item.rectification_photos.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <span style={{ color: 'rgba(0,0,0,0.45)' }}>治理照片：</span>
                          <Space size={8} wrap style={{ marginTop: 6 }}>
                            {item.rectification_photos
                              .map((p) => {
                                if (typeof p === 'string') return { url: p, fileName: null };
                                const url = p?.url || p?.fileUrl || p?.response?.url || p?.response?.file?.url || null;
                                const fileName = p?.fileName || p?.name || p?.response?.fileName || p?.response?.file?.fileName || null;
                                return { url, fileName };
                              })
                              .filter(p => p?.url)
                              .map((p, idx) => (
                                <img
                                  key={`${idx}-${p.url}`}
                                  src={p.url}
                                  alt={p.fileName || '治理照片'}
                                  style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', border: `1px solid ${token.colorBorder}` }}
                                  onClick={() => handlePreviewImage({ url: p.url })}
                                />
                              ))}
                          </Space>
                        </div>
                      )}
                      {(item?.remark || item?.verifier_name || item?.verified_at) && (
                        <div style={{ marginTop: 4, color: 'rgba(0,0,0,0.65)', fontSize: 12 }}>
                          {item?.verifier_name ? `验证人：${item.verifier_name}` : ''}
                          {item?.verified_at ? `  验证时间：${dayjs(item.verified_at).format('YYYY-MM-DD HH:mm')}` : ''}
                          {item?.remark ? `  备注：${item.remark}` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div style={{ color: 'rgba(0,0,0,0.45)' }}>暂无治理记录</div>
          )}
        </div>
        <div style={{ marginTop: 12, color: 'rgba(0,0,0,0.45)' }}>
          双击书签打开详情；核心信息已在书签中展示。
        </div>
      </Modal>

      {/* 图片预览弹窗 */}
      <Modal
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 800}
        style={isMobile ? { top: 16 } : undefined}
        zIndex={2000}
      >
        <AuthedImage
          src={previewImage}
          alt="预览"
          style={{ width: '100%', height: 'auto' }}
        />
      </Modal>

      <Modal
        title="扫一扫"
        open={scanModalVisible}
        onCancel={() => {
          stopScan();
          setScanModalVisible(false);
        }}
        footer={null}
        width={isMobile ? '100%' : 520}
        style={isMobile ? { top: 16 } : undefined}
        destroyOnClose
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: 12, color: 'rgba(0,0,0,0.65)' }}>
            对准风险记录二维码进行扫描
          </div>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', gap: 8 }}>
            <Button
              loading={scanDevicesLoading}
              disabled={scanDevicesLoading || scanDevices.length <= 1}
              style={{ visibility: (scanDevicesLoading || scanDevices.length > 1) ? 'visible' : 'hidden' }}
              onClick={() => {
                if (!scanDevices.length) return;
                const index = scanDevices.findIndex(d => d.deviceId === selectedDeviceId);
                const next = scanDevices[(Math.max(index, 0) + 1) % scanDevices.length];
                if (next?.deviceId) setSelectedDeviceId(next.deviceId);
              }}
            >
              切换摄像头
            </Button>
          </div>
          <div
            style={{
              width: '100%',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#000'
            }}
          >
            <video key={scanSessionId} ref={scanVideoRef} style={{ width: '100%' }} playsInline muted />
          </div>
          {scanError ? (
            <div style={{ marginTop: 12, color: '#ff4d4f' }}>{scanError}</div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
};

export default DormHazardPage;